import { getIssueTitleParts, getProxiedWikipediaImageUrl } from "util/entityUtils";
import { formatActorDisplayName, isDeletedUsernamePlaceholder } from "util/entityDisplayIdentity";
import {
  getIssueProfilePath,
  getPostKindProfilePath,
  getTeamProfilePath,
  getUserProfilePath,
} from "util/entityMetadata";
import type { Action, Feedback, Impact, Issue, Solution, Team, User, WikiSuggestion } from "util/constants";
import type { EntityKind } from "util/posts";
import type {
  AvatarMappingInput,
  AvatarViewModel,
  EntityAvatarEntityType,
  WikipediaSummaryLike,
} from "./entityAvatar.types";
import { crosspostSourceChipClass } from "./entityAvatarTokens";

const WIKI_FALLBACK_IMG = "https://upload.wikimedia.org/wikipedia/en/8/80/Wikipedia-logo-v2.svg";

function withThumbnailProxy(details: AvatarViewModel): AvatarViewModel {
  const rawThumbnail = typeof details.thumbnail === "string" ? details.thumbnail : undefined;
  const proxiedThumbnail = getProxiedWikipediaImageUrl(rawThumbnail);
  if (proxiedThumbnail && proxiedThumbnail !== rawThumbnail) {
    return { ...details, thumbnail: proxiedThumbnail };
  }
  return details;
}

function loadingViewModel(): AvatarViewModel {
  return {
    title: "Loading...",
    thumbnail: undefined,
    link: "/",
    subLink: "",
    username: "",
    userAvatar: null,
    parentEntityType: "",
    parentEntityColor: "eoe-gray",
  };
}

function readPrivacy(entity: { properties?: { privacy?: string } } | null | undefined): string | undefined {
  return entity?.properties?.privacy;
}

/**
 * Replace `~` with arrow for issue/post titles; never throws if value is missing.
 */
function replaceTildeInTitle(value: string | undefined | null): string {
  if (typeof value !== "string") {
    return "";
  }
  return value.replace(/~/g, " → ");
}

/**
 * Replace underscores with spaces for cnames; never throws if value is missing.
 */
function replaceUnderscores(value: string | undefined | null): string {
  if (typeof value !== "string") {
    return "";
  }
  return value.replace(/_/g, " ");
}

/** Lines for the issue headline row on issue-backed posts (not the post `title`). */
interface RelatedIssueHeaderLines {
  titleLine: string;
  subtitleLine?: string;
}

/**
 * Canonical issue placement attached to issue-backed post detail.
 */
interface IssuePlacementRecord {
  placement_kind?: string;
  name?: string;
  cname?: string;
  subject_cname?: string;
  topic_cname?: string;
  properties?: Record<string, unknown>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readIssuePlacement(rec: Record<string, unknown>): IssuePlacementRecord | null {
  const placementValue = rec.placement;
  if (isRecord(placementValue)) {
    const placement = placementValue as IssuePlacementRecord;
    if (!placement.placement_kind || placement.placement_kind === "issue") {
      return placement;
    }
  }

  if (isRecord(rec.related_issue)) {
    return rec.related_issue as IssuePlacementRecord;
  }

  const relatedIssues = Array.isArray(rec.related_issues) ? rec.related_issues : [];
  const firstRelatedIssue = relatedIssues[0];
  if (isRecord(firstRelatedIssue)) {
    return firstRelatedIssue as IssuePlacementRecord;
  }

  if (
    typeof rec.cname === "string" ||
    typeof rec.subject_cname === "string" ||
    typeof rec.topic_cname === "string" ||
    isRecord(rec.issue_properties)
  ) {
    return {
      name: typeof rec.name === "string" ? rec.name : undefined,
      cname: typeof rec.cname === "string" ? rec.cname : undefined,
      subject_cname:
        typeof rec.subject_cname === "string" ? rec.subject_cname : undefined,
      topic_cname: typeof rec.topic_cname === "string" ? rec.topic_cname : undefined,
      properties: isRecord(rec.issue_properties)
        ? (rec.issue_properties as Record<string, unknown>)
        : undefined,
    };
  }

  return null;
}

function formatRelatedIssueHeaderLines(rec: Record<string, unknown>): RelatedIssueHeaderLines | null {
  const placement = readIssuePlacement(rec);
  const resolvedName =
    typeof placement?.name === "string" ? placement.name.trim() : "";
  const resolvedCname =
    typeof placement?.cname === "string" ? placement.cname.trim() : "";
  if (!resolvedName && !resolvedCname) {
    return null;
  }
  const props = placement?.properties as { type?: string } | undefined;
  if (resolvedName && props?.type === "Compound") {
    const parts = getIssueTitleParts(resolvedName);
    return {
      titleLine: replaceUnderscores(replaceTildeInTitle(parts.influencer)),
      subtitleLine:
        parts.influenced !== null && parts.influenced !== undefined
          ? replaceUnderscores(replaceTildeInTitle(parts.influenced))
          : undefined,
    };
  }
  if (resolvedName) {
    return { titleLine: replaceTildeInTitle(resolvedName) };
  }
  return { titleLine: replaceUnderscores(resolvedCname) };
}

/**
 * Fallback for issue-backed impact/teabate when placement and denormalized issue fields are absent.
 * Intentionally does **not** use post `title` / `name` / `entity_name` (those mirror post headline).
 */
function resolveImpactIssueTitleFallback(imRec: Record<string, unknown>, im: Impact): string {
  const placement = readIssuePlacement(imRec);
  if (typeof placement?.subject_cname === "string" && placement.subject_cname.trim().length > 0) {
    return replaceUnderscores(placement.subject_cname);
  }
  if (typeof placement?.cname === "string" && placement.cname.trim().length > 0) {
    return replaceUnderscores(placement.cname);
  }
  if (typeof placement?.topic_cname === "string" && placement.topic_cname.trim().length > 0) {
    return replaceUnderscores(placement.topic_cname);
  }
  if (typeof im.subject_cname === "string" && im.subject_cname.trim().length > 0) {
    return replaceUnderscores(im.subject_cname);
  }
  if (typeof im.cname === "string" && im.cname.trim().length > 0) {
    return replaceUnderscores(im.cname);
  }
  if (typeof im.topic_cname === "string" && im.topic_cname.trim().length > 0) {
    return replaceUnderscores(im.topic_cname);
  }
  const ip = im.issue_properties as Record<string, unknown> | undefined;
  const ipName = ip?.name;
  if (typeof ipName === "string" && ipName.trim().length > 0) {
    return replaceTildeInTitle(ipName);
  }
  return "";
}

/** Profile URL for the related issue (canonical `/issue/:cname`). */
function resolveImpactIssueHref(imRec: Record<string, unknown>, im: Impact): string {
  const placement = readIssuePlacement(imRec);
  const fromPlacement =
    typeof placement?.cname === "string" ? placement.cname.trim() : "";
  if (fromPlacement.length > 0) {
    return getIssueProfilePath(fromPlacement);
  }
  if (typeof im.cname === "string" && im.cname.trim().length > 0) {
    return getIssueProfilePath(im.cname.trim());
  }
  return "/";
}

function resolveImpactTopicSubtitle(
  header: RelatedIssueHeaderLines | null,
  imRec: Record<string, unknown>,
  im: Impact
): string | undefined {
  if (header?.subtitleLine !== undefined) {
    return header.subtitleLine;
  }
  const placement = readIssuePlacement(imRec);
  const fromIssue =
    typeof placement?.topic_cname === "string" && placement.topic_cname.trim().length > 0
      ? replaceUnderscores(placement.topic_cname)
      : undefined;
  if (fromIssue) {
    return fromIssue;
  }
  if (typeof im.topic_cname === "string" && im.topic_cname.trim().length > 0) {
    return replaceUnderscores(im.topic_cname);
  }
  return undefined;
}

function resolveIssuePlacementThumbnail(
  rec: Record<string, unknown>,
  resolvedThumbByKey: Record<string, string>,
  imageCdn: string
): string | undefined {
  const placement = readIssuePlacement(rec);
  const subjectKey =
    typeof placement?.subject_cname === "string" ? placement.subject_cname : undefined;
  const resolvedThumb = subjectKey ? resolvedThumbByKey[subjectKey] : undefined;
  const props = placement?.properties;
  const subjectProps = props?.subject_properties as Record<string, unknown> | undefined;
  return (
    resolvedThumb ||
    (typeof props?.reference_link_img_key === "string"
      ? `${imageCdn}/${props.reference_link_img_key}`
      : undefined) ||
    (subjectProps?.thumbnail as { source?: string } | undefined)?.source ||
    (typeof subjectProps?.wiki_parse_first_img === "string"
      ? subjectProps.wiki_parse_first_img
      : undefined)
  );
}

function wikiSubtitle(summary: WikipediaSummaryLike | null | Record<string, unknown> | undefined): string | undefined {
  if (!summary || typeof summary !== "object") {
    return undefined;
  }
  const s = summary as WikipediaSummaryLike;
  if (typeof s.description === "string" && s.description.length > 0) {
    return s.description;
  }
  const ex = typeof s.extract === "string" ? s.extract.trim() : "";
  if (ex.length > 0) {
    return `${ex.substring(0, 100)}...`;
  }
  return undefined;
}

function wikiThumbSource(
  summary: WikipediaSummaryLike | null | Record<string, unknown> | undefined
): string | undefined {
  if (!summary || typeof summary !== "object") {
    return undefined;
  }
  const t = (summary as WikipediaSummaryLike).thumbnail;
  return typeof t === "string" ? t : undefined;
}

/**
 * Maps enriched entity payloads + resolved media to a stable `AvatarViewModel`.
 * Pure function — no I/O.
 */
export function mapEntityToAvatarViewModel(input: AvatarMappingInput): AvatarViewModel {
  const {
    entityType,
    entityData: raw,
    imageCdn,
    isGeneralEntity,
    customSubtitle,
    crosspostData,
    crosspostTargetEntity,
    resolvedThumbByKey,
    resolvedIssueThumbnail,
    wikipediaSummary,
  } = input;

  if (raw === null || raw === undefined) {
    return loadingViewModel();
  }

  const entityData = raw as Record<string, unknown>;

  switch (entityType as EntityAvatarEntityType) {
    case "issue": {
      const wiki = entityData as unknown as WikiSuggestion;
      if (wiki.type === "wiki") {
        return withThumbnailProxy({
          title: wiki.name,
          subtitle: wiki.short_description,
          thumbnail: wiki.thumbnail || WIKI_FALLBACK_IMG,
          link: wiki.link,
          subLink: "",
          username: "",
          userAvatar: null,
          parentEntityType: "Issue",
          parentEntityColor: "issue",
        });
      }
      const issue = entityData as unknown as Issue;
      if (issue.properties?.type === "Compound") {
        const parts = getIssueTitleParts(issue.name);
        const thumbFromWiki =
          wikipediaSummary &&
          typeof wikipediaSummary === "object" &&
          Object.keys(wikipediaSummary).length > 0 &&
          wikiThumbSource(wikipediaSummary as WikipediaSummaryLike);
        return withThumbnailProxy({
          title: replaceTildeInTitle(parts.influencer),
          subtitle: parts.influenced ?? undefined,
          separator: "→",
          thumbnail: thumbFromWiki || resolvedIssueThumbnail || WIKI_FALLBACK_IMG,
          link: getIssueProfilePath(issue.cname),
          subLink: "",
          username: "",
          userAvatar: null,
          parentEntityType: "Issue",
          parentEntityColor: "issue",
        });
      }
      const thumbFromWiki =
        wikipediaSummary &&
        typeof wikipediaSummary === "object" &&
        Object.keys(wikipediaSummary).length > 0 &&
        wikiThumbSource(wikipediaSummary as WikipediaSummaryLike);
      return withThumbnailProxy({
        title: replaceTildeInTitle(issue.name),
        subtitle: wikiSubtitle(wikipediaSummary as WikipediaSummaryLike),
        separator: undefined,
        thumbnail: thumbFromWiki || resolvedIssueThumbnail || WIKI_FALLBACK_IMG,
        link: getIssueProfilePath(issue.cname),
        subLink: "",
        username: "",
        userAvatar: null,
        parentEntityType: "Issue",
        parentEntityColor: "issue",
      });
    }

    case "comment": {
      const ed = entityData as Record<string, unknown> & {
        entity_type?: string;
        title?: string;
        name?: string;
        subtitle?: string;
        id?: number;
        username?: string;
        properties?: Record<string, unknown>;
        properties_public?: Record<string, unknown>;
      };
      const subjectProps = ed.properties?.subject_properties as Record<string, unknown> | undefined;
      const titles = subjectProps?.titles as { canonical?: string } | undefined;
      const canonical = titles?.canonical;
      const resolvedFromSubject =
        typeof canonical === "string" ? resolvedThumbByKey[canonical] : undefined;
      const refKey = ed.properties?.reference_link_img_key;
      const refImg =
        typeof refKey === "string" && refKey.length > 0 ? `${imageCdn}/${refKey}` : undefined;
      const subThumb = subjectProps?.thumbnail as { source?: string } | undefined;
      const thumb =
        resolvedFromSubject ||
        refImg ||
        subThumb?.source ||
        (typeof subjectProps?.wiki_parse_first_img === "string" ? subjectProps.wiki_parse_first_img : undefined) ||
        WIKI_FALLBACK_IMG;
      const et = ed.entity_type;
      const baseLink =
        typeof et === "string" && typeof ed.id === "number" ? `/${et}/${ed.id}` : "/";
      const displayName = formatActorDisplayName(ed.username, readPrivacy(ed));
      return withThumbnailProxy({
        title: (ed.entity_type === "issue" ? ed.title || ed.name : ed.title) as string,
        subtitle: ed.subtitle,
        separator: " → ",
        thumbnail: thumb,
        link: baseLink,
        subLink: "",
        username: displayName,
        userAvatar:
          typeof ed.properties_public?.avatar_key === "string"
            ? `${imageCdn}/${ed.properties_public.avatar_key}`
            : null,
        parentEntityType: "Issue",
        parentEntityColor: "issue",
      });
    }

    case "impact": {
      const im = entityData as unknown as Impact & { is_comment?: boolean };
      const imRec = entityData as Record<string, unknown>;
      const privacy = readPrivacy(im);
      const displayName = formatActorDisplayName(im.username, privacy);
      const ep = im.entity_properties?.subject_properties as Record<string, unknown> | undefined;
      const refKey = ep?.reference_link_img_key;
      const thumb =
        resolveIssuePlacementThumbnail(imRec, resolvedThumbByKey, imageCdn) ||
        (typeof refKey === "string" ? `${imageCdn}/${refKey}` : undefined) ||
        (ep?.thumbnail as { source?: string } | undefined)?.source ||
        (typeof ep?.wiki_parse_first_img === "string" ? ep.wiki_parse_first_img : undefined) ||
        WIKI_FALLBACK_IMG;
      const issueHeader = formatRelatedIssueHeaderLines(imRec);
      const primaryTitle =
        issueHeader !== null && issueHeader.titleLine.length > 0
          ? issueHeader.titleLine
          : resolveImpactIssueTitleFallback(imRec, im);
      const topicSubtitle = resolveImpactTopicSubtitle(issueHeader, imRec, im);
      const issueLink = resolveImpactIssueHref(imRec, im);
      const isCommentBranch = Boolean(im.is_comment);
      if (isCommentBranch) {
        return withThumbnailProxy({
          title: primaryTitle,
          subtitle: im.title && im.title !== "[no_title]" ? im.title : " ",
          separator: " → ",
          thumbnail: thumb,
          link: issueLink,
          subLink: getPostKindProfilePath("impact" as EntityKind, im.id),
          username: displayName,
          userAvatar:
            typeof im.properties_public?.avatar_key === "string"
              ? `${imageCdn}/${im.properties_public.avatar_key}`
              : null,
          parentEntityType: "Impact",
          parentEntityColor:
            im.rel_type === "impact_negative"
              ? "impact-negative"
              : im.rel_type === "impact_positive"
                ? "impact-positive"
                : "impact-inactive",
        });
      }
      return withThumbnailProxy({
        title: primaryTitle,
        subtitle: topicSubtitle,
        separator: " → ",
        thumbnail: thumb,
        link: issueLink,
        subLink: "",
        username: displayName,
        userAvatar:
          typeof im.properties_public?.avatar_key === "string"
            ? `${imageCdn}/${im.properties_public.avatar_key}`
            : null,
        parentEntityType: "Issue",
        parentEntityColor: "issue",
      });
    }

    case "teabate": {
      const im = entityData as unknown as Impact & { is_comment?: boolean };
      const imRecTeabate = entityData as Record<string, unknown>;
      const privacy = readPrivacy(im);
      const displayName = formatActorDisplayName(im.username, privacy);
      const ep = im.entity_properties?.subject_properties as Record<string, unknown> | undefined;
      const refKey = ep?.reference_link_img_key;
      const thumb =
        resolveIssuePlacementThumbnail(imRecTeabate, resolvedThumbByKey, imageCdn) ||
        (typeof refKey === "string" ? `${imageCdn}/${refKey}` : undefined) ||
        (ep?.thumbnail as { source?: string } | undefined)?.source ||
        (typeof ep?.wiki_parse_first_img === "string" ? ep.wiki_parse_first_img : undefined) ||
        WIKI_FALLBACK_IMG;
      const issueHeaderTeabate = formatRelatedIssueHeaderLines(imRecTeabate);
      const primaryTitleTeabate =
        issueHeaderTeabate !== null && issueHeaderTeabate.titleLine.length > 0
          ? issueHeaderTeabate.titleLine
          : resolveImpactIssueTitleFallback(imRecTeabate, im);
      const topicSubtitleTeabate = resolveImpactTopicSubtitle(issueHeaderTeabate, imRecTeabate, im);
      const issueLinkTeabate = resolveImpactIssueHref(imRecTeabate, im);
      const isCommentBranch = Boolean(im.is_comment);
      if (isCommentBranch) {
        return withThumbnailProxy({
          title: primaryTitleTeabate,
          subtitle: im.title && im.title !== "[no_title]" ? im.title : " ",
          separator: " → ",
          thumbnail: thumb,
          link: issueLinkTeabate,
          subLink: getPostKindProfilePath("teabate" as EntityKind, im.id),
          username: displayName,
          userAvatar:
            typeof im.properties_public?.avatar_key === "string"
              ? `${imageCdn}/${im.properties_public.avatar_key}`
              : null,
          parentEntityType: "Teabate",
          parentEntityColor: "teabate",
        });
      }
      return withThumbnailProxy({
        title: primaryTitleTeabate,
        subtitle: topicSubtitleTeabate,
        separator: " → ",
        thumbnail: thumb,
        link: issueLinkTeabate,
        subLink: "",
        username: displayName,
        userAvatar:
          typeof im.properties_public?.avatar_key === "string"
            ? `${imageCdn}/${im.properties_public.avatar_key}`
            : null,
        parentEntityType: "Teabate",
        parentEntityColor: "teabate",
      });
    }

    case "solution": {
      const sol = entityData as unknown as Solution & { is_comment?: boolean };
      const solRec = entityData as Record<string, unknown>;
      const placement = readIssuePlacement(solRec);
      const issueHeader = formatRelatedIssueHeaderLines(solRec);
      if (!placement && !issueHeader) {
        return {
          title: sol.title || sol.name || "Solution",
          thumbnail: undefined,
          link: "/",
          username: formatActorDisplayName(sol.username, readPrivacy(sol)),
          userAvatar:
            typeof sol.properties_public?.avatar_key === "string"
              ? `${imageCdn}/${sol.properties_public.avatar_key}`
              : null,
          parentEntityType: "Solution",
          parentEntityColor: "solution",
          entitySubtype: sol.type,
        };
      }
      const thumb =
        resolveIssuePlacementThumbnail(solRec, resolvedThumbByKey, imageCdn);
      const privacy = readPrivacy(sol);
      const uname = formatActorDisplayName(sol.username, privacy);
      const postTitle = sol.title || sol.name;
      const postSubtitle =
        postTitle && postTitle !== "[no_title]" ? postTitle : " ";
      const issueTitle =
        issueHeader?.titleLine ||
        (typeof placement?.subject_cname === "string" && placement.subject_cname.length > 0
          ? replaceUnderscores(placement.subject_cname)
          : typeof placement?.name === "string" && placement.name.length > 0
            ? replaceTildeInTitle(placement.name)
            : typeof placement?.cname === "string" && placement.cname.length > 0
              ? replaceUnderscores(placement.cname)
              : postTitle || "Solution");
      const issueSubtitle =
        issueHeader?.subtitleLine ||
        (typeof placement?.topic_cname === "string" && placement.topic_cname.length > 0
          ? replaceUnderscores(placement.topic_cname)
          : undefined);
      const issueLink =
        typeof placement?.cname === "string" && placement.cname.length > 0
          ? getIssueProfilePath(placement.cname)
          : "/";
      if (sol.is_comment) {
        return withThumbnailProxy({
          title: issueTitle,
          subtitle: postSubtitle,
          separator: " → ",
          thumbnail: thumb,
          link: issueLink,
          subLink: getPostKindProfilePath("solution" as EntityKind, sol.id),
          username: isGeneralEntity ? postTitle || "" : uname,
          userAvatar:
            typeof sol.properties_public?.avatar_key === "string"
              ? `${imageCdn}/${sol.properties_public.avatar_key}`
              : null,
          parentEntityType: "Solution",
          parentEntityColor: "solution",
          entitySubtype: sol.type,
        });
      }
      return withThumbnailProxy({
        title: issueTitle,
        subtitle: issueSubtitle,
        separator: " → ",
        thumbnail: thumb,
        link: issueLink,
        username: isGeneralEntity ? postTitle || "" : uname,
        userAvatar:
          typeof sol.properties_public?.avatar_key === "string"
            ? `${imageCdn}/${sol.properties_public.avatar_key}`
            : null,
        parentEntityType: "Issue",
        parentEntityColor: "issue",
        entitySubtype: sol.type,
      });
    }

    case "action": {
      const act = entityData as unknown as Action;
      const actRec = entityData as Record<string, unknown>;
      const placement = readIssuePlacement(actRec);
      const issueHeader = formatRelatedIssueHeaderLines(actRec);
      if (!placement && !issueHeader) {
        return {
          title: act.title || "Action",
          link: "/",
          username: formatActorDisplayName(act.username, readPrivacy(act)),
          userAvatar:
            typeof act.properties_public?.avatar_key === "string"
              ? `${imageCdn}/${act.properties_public.avatar_key}`
              : null,
          parentEntityType: "Action",
          parentEntityColor: "action",
        };
      }
      const thumb = resolveIssuePlacementThumbnail(actRec, resolvedThumbByKey, imageCdn);
      const privacy = readPrivacy(act);
      const uname = formatActorDisplayName(act.username, privacy);
      const issueTitle =
        issueHeader?.titleLine ||
        (typeof placement?.subject_cname === "string" && placement.subject_cname.length > 0
          ? replaceUnderscores(placement.subject_cname)
          : typeof placement?.name === "string" && placement.name.length > 0
            ? replaceTildeInTitle(placement.name)
            : typeof placement?.cname === "string" && placement.cname.length > 0
              ? replaceUnderscores(placement.cname)
              : act.title || "Action");
      const issueSubtitle =
        issueHeader?.subtitleLine ||
        (typeof placement?.topic_cname === "string" && placement.topic_cname.length > 0
          ? replaceUnderscores(placement.topic_cname)
          : undefined);
      const issueLink =
        typeof placement?.cname === "string" && placement.cname.length > 0
          ? getIssueProfilePath(placement.cname)
          : "/";
      if ((act as { is_comment?: boolean }).is_comment) {
        return withThumbnailProxy({
          title: issueTitle,
          subtitle: act.title,
          separator: " → ",
          thumbnail: thumb,
          link: issueLink,
          subLink: getPostKindProfilePath("action" as EntityKind, act.id),
          username: isGeneralEntity ? act.title : uname,
          userAvatar:
            typeof act.properties_public?.avatar_key === "string"
              ? `${imageCdn}/${act.properties_public.avatar_key}`
              : null,
          parentEntityType: "Action",
          parentEntityColor: "action",
        });
      }
      return withThumbnailProxy({
        title: issueTitle,
        subtitle: issueSubtitle,
        separator: " → ",
        thumbnail: thumb,
        link: issueLink,
        username: isGeneralEntity ? act.username : uname,
        userAvatar:
          typeof act.properties_public?.avatar_key === "string"
            ? `${imageCdn}/${act.properties_public.avatar_key}`
            : null,
        parentEntityType: "Issue",
        parentEntityColor: "issue",
      });
    }

    case "collection": {
      /**
       * Collection posts don't carry an issue placement or topic headline, so
       * there's nothing meaningful to put in the avatar's title slot. Mirror
       * the feedback avatar treatment: surface the author's identity as the
       * headline (username + avatar + timestamp) and let the card body carry
       * the collection title via `entity.title`.
       */
      const collection = entityData as Record<string, unknown> & {
        id?: number;
        title?: string | null;
        username?: string;
        properties?: Record<string, unknown>;
        properties_public?: Record<string, unknown>;
      };
      const privacy = readPrivacy(collection as { properties?: { privacy?: string } });
      const displayName = formatActorDisplayName(collection.username, privacy);
      const avatarFromKey =
        typeof collection.properties_public?.avatar_key === "string"
          ? `${imageCdn}/${collection.properties_public.avatar_key}`
          : null;
      const placeholder =
        privacy === "anonymous"
          ? "/avatea_incognito_nobg.png"
          : isDeletedUsernamePlaceholder(collection.username) && privacy !== "anonymous"
            ? "/avatea_deleted.png"
            : null;
      return withThumbnailProxy({
        title: displayName,
        thumbnail: avatarFromKey ?? placeholder,
        link: getPostKindProfilePath("collection", collection.id || 0),
        subLink: "",
        username: displayName,
        userAvatar: avatarFromKey ?? placeholder,
        parentEntityType: "Collection",
        parentEntityColor: "collection",
      });
    }

    case "feedback": {
      const fb = entityData as unknown as Feedback & { is_comment?: boolean };
      const privacy = readPrivacy(fb);
      const displayName = formatActorDisplayName(fb.username, privacy);
      const avatarFromKey =
        typeof fb.properties_public?.avatar_key === "string"
          ? `${imageCdn}/${fb.properties_public.avatar_key}`
          : null;
      const placeholder =
        privacy === "anonymous"
          ? "/avatea_incognito_nobg.png"
          : isDeletedUsernamePlaceholder(fb.username) && privacy !== "anonymous"
            ? "/avatea_deleted.png"
            : null;
      if (fb.is_comment) {
        const sub = fb.title && fb.title !== "[no_title]" ? fb.title : " ";
        const profileLink =
          typeof fb.username === "string" && fb.username.length > 0
            ? getUserProfilePath(fb.username)
            : getPostKindProfilePath("feedback" as EntityKind, fb.id);
        return withThumbnailProxy({
          title: displayName,
          subtitle: sub,
          thumbnail: avatarFromKey ?? placeholder,
          link: profileLink,
          subLink: getPostKindProfilePath("feedback" as EntityKind, fb.id),
          username: displayName,
          userAvatar: avatarFromKey ?? placeholder,
          parentEntityType: "Feedback",
          parentEntityColor: "feedback",
        });
      }
      return withThumbnailProxy({
        title: displayName,
        thumbnail: avatarFromKey ?? placeholder,
        link: getPostKindProfilePath("feedback" as EntityKind, fb.id),
        username: displayName,
        userAvatar: avatarFromKey ?? placeholder,
        parentEntityType: "Feedback",
        parentEntityColor: "feedback",
      });
    }

    case "user": {
      const u = entityData as unknown as User & { user_entity_details?: Record<string, unknown> };
      return withThumbnailProxy({
        title: u.username || u.display_name || `User ${u.id}`,
        subtitle: customSubtitle || u.display_name,
        thumbnail:
          typeof u.properties_public?.avatar_key === "string"
            ? `${imageCdn}/${u.properties_public.avatar_key}`
            : null,
        link: getUserProfilePath(u.username || u.id),
        subLink: "",
        username: u.username ?? "",
        userAvatar:
          typeof u.properties_public?.avatar_key === "string"
            ? `${imageCdn}/${u.properties_public.avatar_key}`
            : null,
        parentEntityType: "User",
        parentEntityColor: "user",
        userEntityDetails: u.user_entity_details as Record<string, unknown> | undefined,
      });
    }

    case "team": {
      const t = entityData as unknown as Team & { user_entity_details?: Record<string, unknown> };
      return withThumbnailProxy({
        title: t.teamname || t.display_name || `Team ${t.id}`,
        subtitle: customSubtitle || t.display_name,
        thumbnail:
          typeof t.properties_public?.avatar_key === "string"
            ? `${imageCdn}/${t.properties_public.avatar_key}`
            : null,
        link: getTeamProfilePath(t.teamname || t.id),
        subLink: "",
        username: t.teamname ?? "",
        userAvatar:
          typeof t.properties_public?.avatar_key === "string"
            ? `${imageCdn}/${t.properties_public.avatar_key}`
            : null,
        parentEntityType: "Team",
        parentEntityColor: "team",
        userEntityDetails: t.user_entity_details as Record<string, unknown> | undefined,
      });
    }

    case "crosspost": {
      if (!crosspostData) {
        return {
          title: "",
          thumbnail: undefined,
          link: "/",
          subLink: "",
          username: "",
          userAvatar: null,
          parentEntityType: "",
          parentEntityColor: "eoe-gray",
        };
      }
      if (crosspostData.sourceType === "issue" && crosspostData.targetType === "issue") {
        const sourceEntity = entityData as unknown as Issue;
        const targetEntity = (crosspostTargetEntity ?? crosspostData.targetEntity) as
          | { attributes?: { name?: string; cname?: string } }
          | undefined;
        const attrs = targetEntity?.attributes;
        const targetName = attrs?.name?.replace(/~/g, " → ") || "Target Issue";
        const targetLink = attrs?.cname ? getIssueProfilePath(attrs.cname) : "/";
        const sp = sourceEntity.properties?.subject_properties as Record<string, unknown> | undefined;
        const subj = sourceEntity.subject_cname;
        const resolvedSubject = typeof subj === "string" ? resolvedThumbByKey[subj] : undefined;
        const thumb =
          resolvedSubject ||
          (typeof sp?.reference_link_img_key === "string"
            ? `${imageCdn}/${sp.reference_link_img_key}`
            : undefined) ||
          (sp?.thumbnail as { source?: string } | undefined)?.source ||
          (typeof sp?.wiki_parse_first_img === "string" ? sp.wiki_parse_first_img : undefined) ||
          WIKI_FALLBACK_IMG;
        const srcPrivacy = readPrivacy(sourceEntity);
        return withThumbnailProxy({
          title: sourceEntity.name || "Source Issue",
          subtitle: targetName,
          thumbnail: thumb,
          link: getIssueProfilePath(sourceEntity.cname),
          subLink: targetLink,
          username: formatActorDisplayName(sourceEntity.username, srcPrivacy),
          userAvatar:
            typeof sourceEntity.properties_public?.avatar_key === "string"
              ? `${imageCdn}/${sourceEntity.properties_public.avatar_key}`
              : null,
          parentEntityType: "Resteep",
          parentEntityColor: "issue",
        });
      }
      if (crosspostData.sourceType === "issue" && crosspostData.targetType === "user") {
        const sourceEntity = entityData as unknown as Issue;
        const sp = sourceEntity.properties?.subject_properties as Record<string, unknown> | undefined;
        const subj = sourceEntity.subject_cname;
        const resolvedSubject = typeof subj === "string" ? resolvedThumbByKey[subj] : undefined;
        const thumb =
          resolvedSubject ||
          (typeof sp?.reference_link_img_key === "string"
            ? `${imageCdn}/${sp.reference_link_img_key}`
            : undefined) ||
          (sp?.thumbnail as { source?: string } | undefined)?.source ||
          (typeof sp?.wiki_parse_first_img === "string" ? sp.wiki_parse_first_img : undefined) ||
          WIKI_FALLBACK_IMG;
        const srcPrivacy = readPrivacy(sourceEntity);
        return withThumbnailProxy({
          title: sourceEntity.name || "Issue",
          thumbnail: thumb,
          link: getIssueProfilePath(sourceEntity.cname),
          username: formatActorDisplayName(sourceEntity.username, srcPrivacy),
          userAvatar:
            typeof sourceEntity.properties_public?.avatar_key === "string"
              ? `${imageCdn}/${sourceEntity.properties_public.avatar_key}`
              : null,
          parentEntityType: "Issue",
          parentEntityColor: "issue",
        });
      }
      if (crosspostData.sourceType !== "comment" && crosspostData.sourceType !== "feedback") {
        const sourceEntity = (crosspostData.sourceEntity ?? entityData) as Record<string, unknown>;
        const attrs = sourceEntity.attributes as Record<string, unknown> | undefined;
        const attrProps = attrs?.properties as { privacy?: string } | undefined;
        const sourceTitle = (attrs?.title ?? attrs?.name) as string | undefined;
        const shouldShowSubtitle = Boolean(sourceTitle && sourceTitle !== "[no_title]");
        const relatedIssues = attrs?.related_issues as Array<Record<string, unknown>> | undefined;
        const firstRi = relatedIssues?.[0];
        const relatedIssue =
          firstRi ||
          ({
            cname: attrs?.subject_cname ?? attrs?.cname,
            name:
              typeof attrs?.subject_cname === "string"
                ? String(attrs.subject_cname).replace(/_/g, " ")
                : attrs?.name,
            properties: {},
          } as Record<string, unknown>);
        const riName = relatedIssue.name as string | undefined;
        const riCname = relatedIssue.cname as string | undefined;
        const rp = relatedIssue.properties as Record<string, unknown> | undefined;
        const sp = rp?.subject_properties as Record<string, unknown> | undefined;
        const subj = typeof relatedIssue.subject_cname === "string" ? relatedIssue.subject_cname : riCname;
        const resolvedRi = typeof subj === "string" ? resolvedThumbByKey[subj] : undefined;
        const thumb =
          resolvedRi ||
          (typeof rp?.reference_link_img_key === "string"
            ? `${imageCdn}/${rp.reference_link_img_key}`
            : undefined) ||
          (sp?.thumbnail as { source?: string } | undefined)?.source ||
          (typeof sp?.wiki_parse_first_img === "string" ? sp.wiki_parse_first_img : undefined) ||
          WIKI_FALLBACK_IMG;
        const st = crosspostData.sourceType;
        const chipColor = crosspostSourceChipClass(st);
        const attrsPub = attrs?.properties_public as Record<string, unknown> | undefined;
        const avKey = attrsPub?.avatar_key;
        return withThumbnailProxy({
          title: riName || (riCname ? riCname.replace(/_/g, " ") : "Related Issue"),
          subtitle: shouldShowSubtitle ? sourceTitle : undefined,
          customChip: shouldShowSubtitle
            ? {
                text: st.charAt(0).toUpperCase() + st.slice(1),
                color: chipColor,
              }
            : undefined,
          thumbnail: thumb,
          link: riCname ? getIssueProfilePath(riCname) : "/",
          username: formatActorDisplayName(
          attrs?.username as string | undefined,
          attrProps?.privacy ?? readPrivacy(sourceEntity as { properties?: { privacy?: string } })
        ),
          userAvatar: typeof avKey === "string" ? `${imageCdn}/${avKey}` : null,
          parentEntityType: "Issue",
          parentEntityColor: "issue",
        });
      }
      const fallback = entityData as Record<string, unknown>;
      return {
        title: (fallback.name as string) || "Resteep",
        thumbnail: undefined,
        link: "/",
        subLink: "",
        username: formatActorDisplayName(fallback.username as string | undefined, readPrivacy(fallback as { properties?: { privacy?: string } })),
        userAvatar: null,
        parentEntityType: "Resteep",
        parentEntityColor: "issue",
      };
    }

    case "wiki": {
      const wiki = entityData as unknown as WikiSuggestion;
      return withThumbnailProxy({
        title: wiki.name,
        subtitle: wiki.short_description,
        thumbnail: wiki.thumbnail || WIKI_FALLBACK_IMG,
        link: wiki.link,
        subLink: "",
        username: "",
        userAvatar: null,
        parentEntityType: "Issue",
        parentEntityColor: "issue",
      });
    }

    default:
      return {
        title: "",
        thumbnail: undefined,
        link: "/",
        subLink: "",
        username: "",
        userAvatar: null,
        parentEntityType: "",
        parentEntityColor: "eoe-gray",
      };
  }
}
